// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: pictures in one chat open as one set in the media viewer, so arrows, arrow keys and swipes
// step through the whole chat rather than just the picture that was tapped. A chat wraps its
// messages in <MediaGallery>; each picture registers with useMediaGalleryItem and opens through it.
// Order is the order on screen, so it needs no knowledge of how a chat sorts or groups messages.
// Outside a gallery a picture opens on its own, as before.
// The viewer opens at once on the pictures that are ready; ones still loading (an album's hidden
// pictures) join the set when they arrive, so a tap never waits on the rest of the chat.

import {
	type MediaViewerFile,
	openMediaViewerForFiles,
	updateMediaViewerFiles,
} from '@app/features/ui/commands/MediaViewerCommands';
import {createContext, type ReactNode, useCallback, useContext, useEffect, useId, useRef, useState} from 'react';

/** A picture in a gallery; `key` picks it out when one entry holds several (an album). */
export type MediaGalleryFile = MediaViewerFile & {key?: string};

type Group = ReadonlyArray<MediaGalleryFile>;

/** Pictures ready now plus the whole group once the rest have loaded. */
export interface MediaGalleryPartialGroup {
	ready: Group;
	all: Promise<Group>;
}

type Files = () => Group | Promise<Group> | MediaGalleryPartialGroup;

interface Read {
	/** What can be shown now, or null while nothing is. */
	ready: Group | null;
	/** The finished group when it is still loading. */
	all: Promise<Group> | null;
}

function readEntry(entry: Entry): Read {
	let result: ReturnType<Files>;
	try {
		result = entry.files();
	} catch {
		return {ready: [], all: null};
	}
	if (result instanceof Promise) return {ready: null, all: result.catch(() => [])};
	if (Array.isArray(result)) return {ready: result, all: null};
	const partial = result as MediaGalleryPartialGroup;
	return {ready: partial.ready, all: partial.all.catch(() => partial.ready)};
}

interface Entry {
	element: Element | null;
	files: Files;
}

function byScreenOrder(a: Entry, b: Entry): number {
	if (!a.element || !b.element || a.element === b.element) return 0;
	return a.element.compareDocumentPosition(b.element) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
}

export class MediaGalleryRegistry {
	private readonly entries = new Map<string, Entry>();

	set(id: string, entry: Entry): void {
		this.entries.set(id, entry);
	}

	delete(id: string): void {
		this.entries.delete(id);
	}

	/** Opens the viewer on entry `id` (on its file `key`, if given) with every picture in the gallery. */
	async open(id: string, key?: string): Promise<void> {
		const own = this.entries.get(id);
		if (!own) return;
		const ordered = [...this.entries.values()]
			.filter((entry) => entry === own || entry.element?.isConnected)
			.sort(byScreenOrder);
		const reads = ordered.map(readEntry);
		const at = ordered.indexOf(own);
		const flatten = (groups: ReadonlyArray<Group>) => {
			const files: Array<MediaViewerFile> = [];
			let start = -1;
			for (const [index, group] of groups.entries()) {
				if (index === at) {
					const picked = key === undefined ? 0 : group.findIndex((file) => file.key === key);
					if (group.length) start = files.length + Math.max(0, picked);
				}
				files.push(...group);
			}
			return {files, start};
		};
		const later = reads.some((read) => read.all);
		const whole = later
			? Promise.all(reads.map((read) => read.all ?? Promise.resolve(read.ready ?? [])))
			: Promise.resolve(reads.map((read) => read.ready ?? []));
		// The tapped picture's own group has to be there to open on; the others can follow.
		const ownReady = reads[at].ready;
		const tapped = ownReady && (key === undefined || ownReady.some((file) => file.key === key));
		if (!tapped) {
			const {files, start} = flatten(await whole);
			if (start >= 0) await openMediaViewerForFiles(files, start);
			return;
		}
		const now = flatten(reads.map((read) => read.ready ?? []));
		if (now.start < 0) return;
		const session = await openMediaViewerForFiles(now.files, now.start);
		if (session == null || !later) return;
		void whole.then((groups) => updateMediaViewerFiles(session, flatten(groups).files));
	}
}

const MediaGalleryContext = createContext<MediaGalleryRegistry | null>(null);

/** Whether this sits inside a MediaGallery, so a part of it can join that set instead of making its own. */
export function useInMediaGallery(): boolean {
	return useContext(MediaGalleryContext) !== null;
}

export function MediaGallery({children}: {children: ReactNode}) {
	const [registry] = useState(() => new MediaGalleryRegistry());
	return <MediaGalleryContext.Provider value={registry}>{children}</MediaGalleryContext.Provider>;
}

/**
 * Registers a picture (or, with a function, a group such as an album) with the surrounding gallery.
 * Put `ref` on the element shown in the chat; `open` opens the viewer there, alone when there is no
 * gallery or nothing to show yet.
 */
export function useMediaGalleryItem(files: MediaGalleryFile | Files | null): {
	ref: (element: Element | null) => void;
	open: (key?: string) => void;
} {
	const registry = useContext(MediaGalleryContext);
	const id = useId();
	const element = useRef<Element | null>(null);
	const latest = useRef(files);
	latest.current = files;
	const read: Files = useCallback(() => {
		const current = latest.current;
		if (!current) return [];
		return typeof current === 'function' ? current() : [current];
	}, []);
	const present = files != null;
	useEffect(() => {
		if (!registry || !present) return;
		registry.set(id, {
			get element() {
				return element.current;
			},
			files: read,
		});
		return () => registry.delete(id);
	}, [id, present, read, registry]);
	const ref = useCallback((node: Element | null) => {
		element.current = node;
	}, []);
	const open = useCallback(
		(key?: string) => {
			if (!latest.current) return;
			if (registry) {
				void registry.open(id, key);
				return;
			}
			const {ready, all} = readEntry({element: null, files: read});
			void (async () => {
				const tapped = ready?.length && (key === undefined || ready.some((file) => file.key === key));
				const group = tapped ? ready : await (all ?? Promise.resolve([]));
				const picked = key === undefined ? 0 : group.findIndex((file) => file.key === key);
				const session = await openMediaViewerForFiles(group, Math.max(0, picked));
				if (session != null && tapped && all) void all.then((rest) => updateMediaViewerFiles(session, rest));
			})();
		},
		[id, read, registry],
	);
	return {ref, open};
}
